import type { PoQueryConfig } from '@gov-nx/api';
import { useAxiosInstance } from '@gov-nx/api';
import type { DocumentDownload } from '@gov-nx/api';
import {
	downloadDocumentImageQuery,
	downloadDocumentQuery,
	useDownloadPoMutation,
} from '@gov-nx/api';
import { useDownloadEvents } from '@gov-nx/core/events';
import { useMap } from '@gov-nx/utils/common';

export interface DocumentDownloadProps {
	onError?: (
		error: Error,
		variables: {
			documentId: number;
			souborId: string;
		}
	) => void;
	onSuccess?: (
		response: DocumentDownload<{
			documentId: number;
			souborId: string;
		}>
	) => void;
	config?: PoQueryConfig;
}

export const useDocumentDownload = (props?: DocumentDownloadProps) => {
	const { axiosInstance } = useAxiosInstance();

	return useDownloadPoMutation(downloadDocumentQuery(axiosInstance), {
		onError: (error, variables) => props?.onError?.(error, variables),
		onSuccess: async (response) => {
			props?.onSuccess && props.onSuccess(response);
		},
		config: props?.config,
	});
};

export interface DocumentImageDownloadProps {
	onError?: (error: Error) => void;
	onSuccess?: (blob: Blob) => void;
	config?: PoQueryConfig;
}

export const useDocumentImageDownload = (
	props?: DocumentImageDownloadProps
) => {
	const { axiosInstance } = useAxiosInstance();

	return useDownloadPoMutation(downloadDocumentImageQuery(axiosInstance), {
		onError: props?.onError,
		onSuccess: async (response) => {
			props?.onSuccess && props.onSuccess(response.blob);
		},
		config: props?.config,
	});
};

export const useDocumentsDownload = () => {
	const { downloadDocument } = useDownloadEvents();
	const isDownloading = useMap<number, boolean>();

	const prepare = ({
		documentId,
		souborId,
	}: {
		documentId: number;
		souborId: string;
	}) => ({
		documentId,
		onDownload: () => {
			isDownloading.set(documentId, true);
			return downloadDocument({
				documentId,
				souborId,
				callback: () => {
					isDownloading.set(documentId, false);
				},
			});
		},
		isDownloading: isDownloading.get(documentId) ?? false,
	});

	return { prepare };
};
